import { useEffect, useState } from "react"
import { t } from 'i18next'
import SplitPane, { Pane } from "split-pane-react"
import Editor from "@/client/components/Editor/Editor"
import { minSidebarWidth, minSplitterWidth } from '@/client/config/constants'
import { Capabilities, hasPermission } from "@/shared"
import { useConverter } from "./EditConverterList"
import { converterDataDeclaration } from "./converterData"
import { Container } from "rsuite"

const EditorSplitStyle = {
    display: 'flex',
    width: '100%',
    height: '100%',
}
const ErrorStyle = {
    margin: '0 1em 1em',
    overflow: 'auto',
    height: '100%',
}
const ErrorInnerStyle = {
    //marginBottom: '1em',
}

export function Errors() {
    const { tab, data, classMethods, rowData } = useConverter()

    // map field like 'c0' to 'Nr.'
    const mapField = (key: string) => {
        const result = data.outTable.mapping.find((item: any) => item.column == key)
        return result?.field ?? key
    }

    //
    // Does this rule still name a function the code defines?
    //
    // ⚠️ Asked of rowData, not of data.mapping. data.mapping is the server's
    // answer from the moment the converter was loaded and is never written
    // again — no editing path updates it, not the table, not the suggestions,
    // not the rename dialog. Reading it here meant the warnings kept naming a
    // function that had been corrected long ago, and stayed silent about one
    // that had just been broken. Found on 06.09.2026; the same list the table
    // draws from is the only one that can be right.
    //
    // A row being edited is skipped: switching its type to "function" writes
    // that immediately while `source` still holds the previous value, so
    // warning about it would be warning about a half-finished sentence.
    //
    const checkFunction = (item: any) => {
        if (item.status === 'EDIT') return true
        if (item.type != 'function') return true

        for (let i = 0; i < classMethods.length; i++) {
            if (classMethods[i] == item.source) {
                return true
            }
        }

        return false
    }

    const ShowErrors = () => {
        if (!tab.store?.errors || tab.store?.errors?.length == 0) return t('No errors.')

        return tab.store?.errors?.map((item: any, index: number) => {
            return <div key={index}>{
                '- ' + item.message + ' (' + item.startLineNumber + ',' + item.startColumn + ')'
            }</div>
        })
    }
    const ShowWarnings = () => {
        const warnings: any[] = []

        tab.store?.warnings?.forEach((item: any) => {
            warnings.push(<div key={warnings.length}>{
                '- ' + item.message + ' (' + item.startLineNumber + ',' + item.startColumn + ')'
            }</div>)
        })
        rowData.forEach((item: any) => {
            if (!checkFunction(item)) {
                warnings.push(<div key={warnings.length}>{
                    t('- Invalid function "{item}" in field "{field}"')
                        .replace('{item}', item.source)
                        .replace('{field}', mapField(item.target))
                }</div>)
            }
        })
        if (warnings.length == 0) return t('No warnings.')

        return warnings.map(item => item)
    }

    return (
        <Container style={ErrorStyle}>
            <div style={ErrorInnerStyle}>
                <h4>{t('Errors')}</h4>
                <ShowErrors />

                <h4 style={{ marginTop: '1em' }}>{t('Warnings')}</h4>
                <ShowWarnings />
            </div>
        </Container>

    )
}

export default function CodeEditor() {
    const [sizes, setSizes] = useState(['30%', 200, 'auto'])
    const { tab, data, openEditor, setEditorDataHasChanged } = useConverter()

    useEffect(() => {
        tab.store.errors = []
        tab.store.warnings = []
        tab.store.markers = []
    }, [])

    // made anew on every render: the query builder and the columns may have
    // changed since the editor was opened (operator, 14.10.2026)
    const dataDeclaration = converterDataDeclaration([
        ...(data?.inTable?.mapping ?? []).map((item: any) => item.field),
        ...(data?.query?.projections ?? []),
    ])

    function handleOnChange(value: string | undefined, ev: any): void {
        data.code = value
        setEditorDataHasChanged(value => value + 1)
    }

    function handleOnValidate(errors: any[], warnings: any[], markers: any[]) {
        tab.store.errors = errors
        tab.store.warnings = warnings
        tab.store.markers = markers
        setEditorDataHasChanged(value => value + 1)
    }

    return (
        <div className="editor-splitter" style={openEditor ? EditorSplitStyle : { display: 'none' }}>
            <SplitPane
                split='vertical'
                sizes={sizes}
                onChange={setSizes}
                sashRender={() => null}
            >
                <Pane minSize={minSidebarWidth}>
                    {/* readOnly is not convert_write: that is the capability for
                        assigning a column, and every role but Viewer holds it. Writing
                        code is its own thing with its own capability — enforced on the
                        server as well, so this only spares the user a refused save. */}
                    <Editor
                        className="editor-fullsize"
                        code={data?.code}
                        dataDeclaration={dataDeclaration}
                        readOnly={!hasPermission(Capabilities.converter_code_write)}
                        onChange={handleOnChange}
                        onValidate={handleOnValidate}
                    />
                </Pane>
                <Pane minSize={minSplitterWidth}>
                    <Errors />
                </Pane>
            </SplitPane>
        </div>
    )
}